import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  Ban,
  Download,
  FileCog,
  Inbox,
  Send,
  Sparkles,
  Trash2,
  Wallet,
} from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { SearchableSelect } from "@/components/ui/SearchableSelect";
import { Spinner } from "@/components/ui/Spinner";
import { useAuthStore } from "@/store/auth";
import { useInternalUsers } from "@/features/tasks/hooks/useInternalUsers";
import { EmptyState, PageHeader, SectionCard } from "../components/ui";
import { ExtractionNotes, taskErrorTail } from "../components/wypisy/ExtractionNotes";
import { ExtractionReviewModal } from "../components/wypisy/ExtractionReviewModal";
import { RequestFeeModal } from "../components/wypisy/RequestFeeModal";
import { WypisPoaFeeModal } from "../components/wypisy/WypisPoaFeeModal";
import { EditDooModal } from "../components/DooFeeAttach";
import { ResultFilesUpload } from "../components/ResultFilesUpload";
import { OrderHistorySection } from "../components/orders/OrderHistorySection";
import { OrderOrdererControl } from "../components/orders/OrderOrdererControl";
import { OrderRevertControl } from "../components/orders/OrderRevertControl";
import { WypisStatusBadge } from "../components/wypisy/WypisStatusBadge";
import { WypisTimeline } from "../components/wypisy/WypisTimeline";
import { useTaskPolling, useWypisOrder, useWypisOrderMutations } from "../hooks";
import { fmtDate, fmtDateTime } from "../utils";
import type { ManagedFileMini } from "../types";
import { WYPIS_APPLICANT_LABELS, WYPIS_ORDER_STATUS_LABELS } from "../types";

const FEE_STATUS_LABELS: Record<string, string> = {
  pending_approval: "Oczekuje na akceptację",
  approved: "Zaakceptowana — do opłacenia",
  rejected: "Odrzucona",
  payment_uploaded: "Opłacona",
  acknowledged: "Opłacona i potwierdzona",
  withdrawn: "Wycofana",
};

function FileLink({ file, label }: { file: ManagedFileMini | null; label: string }) {
  if (!file?.url) return null;
  return (
    <a
      href={file.url}
      download={file.original_filename}
      className="inline-flex items-center gap-1 text-xs font-mono border border-[var(--color-border-strong)] px-2 py-1 hover:bg-[var(--color-bg-elevated)]"
    >
      <Download size={12} /> {label}
    </a>
  );
}

export function WypisOrderPage() {
  const { orderId } = useParams<{ orderId: string }>();
  const qc = useQueryClient();
  const me = useAuthStore((s) => s.user);
  const { data: order, isLoading } = useWypisOrder(orderId);
  const m = useWypisOrderMutations(orderId);
  const { data: internalUsers } = useInternalUsers();
  const contactOptions = useMemo(
    () => (internalUsers ?? []).map((u) => ({ value: String(u.id), label: u.full_name || u.username })),
    [internalUsers],
  );

  const [generateTaskId, setGenerateTaskId] = useState<string | null>(null);
  const [extractTaskId, setExtractTaskId] = useState<string | null>(null);
  const generateTask = useTaskPolling(generateTaskId);
  const extractTask = useTaskPolling(extractTaskId);
  const [feeModal, setFeeModal] = useState(false);
  const [editDooModal, setEditDooModal] = useState(false);
  const [poaFeeModal, setPoaFeeModal] = useState(false);
  const [reviewModal, setReviewModal] = useState(false);
  const [actionError, setActionError] = useState("");

  // A finished background task means the order rows changed — refetch the order
  // (files after generation, extraction_proposal after the AI pass).
  useEffect(() => {
    const done = (s?: string) => s && ["success", "failure", "cancelled"].includes(s);
    if ((generateTaskId && done(generateTask.data?.status)) || (extractTaskId && done(extractTask.data?.status))) {
      qc.invalidateQueries({ queryKey: ["pp", "wypis-order", orderId] });
    }
  }, [generateTask.data?.status, extractTask.data?.status, generateTaskId, extractTaskId, orderId, qc]);

  // Auto-open the review modal when the extraction task lands successfully.
  useEffect(() => {
    if (extractTaskId && extractTask.data?.status === "success") setReviewModal(true);
  }, [extractTaskId, extractTask.data?.status]);

  // Lustro bramki backendu: autor, jego następca (osoba do kontaktów po nieaktywnym
  // autorze), dyrektor, CEO — nie zgadujemy tego z `created_by`.
  const canManage = !!order?.can_manage;

  if (isLoading || !order) {
    return <div className="py-16 flex justify-center"><Spinner /></div>;
  }

  const generating = !!generateTaskId && !["success", "failure", "cancelled"].includes(generateTask.data?.status ?? "");
  const extracting = !!extractTaskId && !["success", "failure", "cancelled"].includes(extractTask.data?.status ?? "");
  const parcelsByProject = order.parcels_brief.reduce<Record<string, typeof order.parcels_brief>>(
    (acc, p) => {
      (acc[p.project_name] = acc[p.project_name] ?? []).push(p);
      return acc;
    },
    {},
  );

  const runAction = (fn: () => void) => { setActionError(""); fn(); };
  const onActionError = (e: unknown) => {
    const detail =
      (e as { response?: { data?: { errors?: { detail?: unknown } } } })?.response?.data?.errors
        ?.detail;
    setActionError(typeof detail === "string" ? detail : "Operacja nie powiodła się.");
  };

  return (
    <div className="space-y-4">
      <PageHeader
        title={
          <span className="flex items-center gap-3">
            {order.number} <WypisStatusBadge status={order.status} />
          </span>
        }
        subtitle={`Powiat ${order.county}${order.voivodeship ? ` · woj. ${order.voivodeship}` : ""} · ${order.starostwo_name || "brak adresata"} · wnioskodawca: ${order.applicant_display || WYPIS_APPLICANT_LABELS[order.applicant] || "ELEKTIM"}`}
        backTo="/power-projects/zamawianie?tab=wypisy"
        backLabel="Wypisy EGiB"
        actions={
          canManage && order.status === "new" ? (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => runAction(() => m.cancel.mutate(order.id, { onError: onActionError }))}
            >
              <Ban size={13} className="mr-1" /> Anuluj zamówienie
            </Button>
          ) : undefined
        }
      />

      <WypisTimeline order={order} />
      <OrderOrdererControl
        ordererId={order.created_by}
        ordererName={order.created_by_name}
        ordererPhone={order.created_by_phone}
        ordererEmail={order.created_by_email}
        takenOverByName={order.taken_over_by_name}
        takenOverByPhone={order.taken_over_by_phone}
        takenOverByEmail={order.taken_over_by_email}
        canManage={canManage}
        pending={m.changeOrderer.isPending}
        onChange={(userId) =>
          runAction(() =>
            m.changeOrderer.mutate({ id: order.id, userId }, { onError: onActionError }),
          )
        }
      />
      {/* Cofanie etapów — cel wyznacza backend; przy „zakończonym" to jedyna droga
          z powrotem, bo status zakończony jest terminalny w biegu naprzód. */}
      {canManage && (
        <OrderRevertControl
          status={order.status}
          revertTo={order.revert_to}
          revertLabel={order.revert_label}
          statusLabels={WYPIS_ORDER_STATUS_LABELS}
          hints={{
            // Cofnięcie zakończenia NIE wypisuje danych z działek — apply je tam wpisał.
            completed: "dane wprowadzone do działek zostają — zmienia się tylko status zamówienia",
          }}
          pending={m.revert.isPending}
          onRevert={(reason) =>
            runAction(() =>
              m.revert.mutate({ id: order.id, reason }, { onError: onActionError }),
            )
          }
        />
      )}
      {actionError && <div className="text-xs text-red-600">{actionError}</div>}

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 items-start">
        <div className="space-y-4">
          {/* ── działki ── */}
          <SectionCard title={`Działki (${order.parcel_count})`}>
            <div className="space-y-2">
              {Object.entries(parcelsByProject).map(([project, parcels]) => (
                <div key={project}>
                  <Link
                    to={`/power-projects/${parcels[0].project_id}?tab=parcels`}
                    className="text-xs font-bold hover:underline"
                  >
                    {project}
                  </Link>
                  <div className="mt-1 flex flex-wrap gap-1">
                    {parcels.map((p) => (
                      <span
                        key={p.id}
                        className="px-2 py-0.5 text-[11px] font-mono border border-[var(--color-border)] bg-[var(--color-bg-elevated)]"
                        title={`obręb ${p.obreb || "—"} · gmina ${p.commune || "—"}`}
                      >
                        {p.teryt}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </SectionCard>

          {/* ── dokumenty ── */}
          <SectionCard
            title="Dokumenty wniosku"
            action={
              !["sent", "received", "completed", "cancelled"].includes(order.status) ? (
                <Button
                  size="sm"
                  loading={generating || m.generate.isPending}
                  onClick={() =>
                    runAction(() =>
                      m.generate.mutate(order.id, {
                        onSuccess: (r) => setGenerateTaskId(r.task_id),
                        onError: onActionError,
                      }),
                    )
                  }
                >
                  <FileCog size={13} className="mr-1" />
                  {order.has_documents ? "Generuj ponownie" : "Generuj dokumenty"}
                </Button>
              ) : undefined
            }
          >
            {!["sent", "received", "completed", "cancelled"].includes(order.status) && (
              <label className="block mb-3 max-w-sm">
                <span className="section-label">Osoba do kontaktu z firmy (poz. 5 i 12 formularza)</span>
                <SearchableSelect
                  options={contactOptions}
                  value={order.contact_person ?? (me ? String(me.id) : "")}
                  placeholder="Wybierz osobę…"
                  onChange={(e) =>
                    m.update.mutate({ id: order.id, payload: { contact_person: e.target.value } })
                  }
                />
                {order.has_documents && (
                  <span className="text-[11px] text-[var(--color-text-muted)]">
                    Po zmianie wygeneruj dokumenty ponownie.
                  </span>
                )}
              </label>
            )}
            {generating ? (
              <div className="flex items-center gap-2 text-xs py-2">
                <Spinner size="sm" /> Generuję dokumenty…
              </div>
            ) : order.has_documents ? (
              <div className="space-y-2">
                <div className="flex flex-wrap gap-2">
                  <FileLink file={order.form_file} label="Wniosek (PDF)" />
                  <FileLink file={order.attachment_pdf} label="Załącznik nr 1 (PDF)" />
                  <FileLink file={order.attachment_xlsx} label="Załącznik nr 1 (XLSX)" />
                </div>
                <div className="text-[11px] text-[var(--color-text-muted)]">
                  Wygenerowano {fmtDateTime(order.generated_at)} — sprawdź wizualnie wypełnienie
                  formularza przed wysyłką.
                </div>
              </div>
            ) : (
              <EmptyState>
                {generateTask.data?.status === "failure"
                  ? "Generacja nie powiodła się — spróbuj ponownie."
                  : "Brak wygenerowanych dokumentów"}
              </EmptyState>
            )}
          </SectionCard>

          {/* ── opłaty za pełnomocnictwo (17 zł/szt., osobne od DOO; może być kilka —
                 działki kilku klientów → kilku mocodawców) ── */}
          <SectionCard
            title="Opłata za pełnomocnictwo (17 zł)"
            action={
              canManage && !["sent", "received", "completed", "cancelled"].includes(order.status) ? (
                <Button size="sm" variant="secondary" onClick={() => setPoaFeeModal(true)}>
                  <Wallet size={13} className="mr-1" />
                  {order.poa_fees.length > 0
                    ? "Dodaj kolejną"
                    : order.poa_fee_not_required
                      ? "Zmień"
                      : "Rozlicz"}
                </Button>
              ) : undefined
            }
          >
            {order.poa_fee_not_required ? (
              <div className="text-xs text-[var(--color-text-secondary)]">
                Oznaczona jako niepotrzebna
                {order.poa_fee_not_required_reason ? ` — ${order.poa_fee_not_required_reason}` : ""}.
              </div>
            ) : order.poa_fees.length > 0 ? (
              <div className="space-y-2 text-xs">
                {order.poa_fees.map((poaFee) => (
                  <div key={poaFee.id} className="space-y-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge
                        color={
                          poaFee.status === "payment_uploaded" || poaFee.status === "acknowledged"
                            ? "green"
                            : poaFee.status === "rejected" || poaFee.status === "withdrawn"
                              ? "red"
                              : "yellow"
                        }
                      >
                        {FEE_STATUS_LABELS[poaFee.status] ?? poaFee.status}
                      </Badge>
                      <span className="font-mono">{poaFee.amount} PLN</span>
                      <span className="text-[var(--color-text-secondary)]">{poaFee.office_name}</span>
                      {poaFee.paid_at && (
                        <span className="text-[var(--color-text-secondary)]">
                          opłacono {fmtDate(poaFee.paid_at)}
                        </span>
                      )}
                      <Link to={`/official-fees/${poaFee.id}`} className="hover:underline text-[var(--color-primary)]">
                        otwórz w /official-fees →
                      </Link>
                      {poaFee.payment_files.map((f) =>
                        f.url ? (
                          <a
                            key={f.id}
                            href={f.url}
                            download={f.original_filename}
                            title={`Pobierz: ${f.original_filename}`}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded bg-red-600 hover:bg-red-700 text-white text-xs font-medium"
                          >
                            <Download size={12} /> PDF
                          </a>
                        ) : null
                      )}
                      {canManage && !["sent", "received", "completed", "cancelled"].includes(order.status) && (
                        <button
                          className="ml-auto text-[var(--color-text-muted)] hover:text-red-600"
                          title="Odepnij opłatę od zamówienia (opłata pozostaje w /official-fees)"
                          onClick={() =>
                            runAction(() =>
                              m.setPoaFee.mutate(
                                { id: order.id, payload: { mode: "detach", fee: poaFee.id } },
                                { onError: onActionError },
                              ),
                            )
                          }
                        >
                          <Trash2 size={13} />
                        </button>
                      )}
                    </div>
                    {poaFee.rejection_comment && (
                      <div className="text-red-600">Powód odrzucenia: {poaFee.rejection_comment}</div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <EmptyState>
                Wymagana domyślnie (ELEKTIM jako pełnomocnik inwestora) — podepnij/poproś o
                opłatę albo oznacz jako niepotrzebną przed wysyłką. Gdy działki należą do
                kilku klientów, dodaj osobną opłatę dla każdego mocodawcy.
              </EmptyState>
            )}
          </SectionCard>

          {/* ── opłata ── */}
          <SectionCard
            title="Opłata urzędowa (DOO)"
            action={
              // „sent" jest tu równie ważne co „new": DOO wystawia starostwo dopiero
              // PO otrzymaniu wniosku, więc dokument, na podstawie którego płacimy,
              // przed wysyłką zwykle jeszcze nie istnieje.
              canManage && order.status !== "cancelled" ? (
                <div className="flex items-center gap-2">
                  {(order.fee_id || order.doo_file) && (
                    <Button size="sm" variant="secondary" onClick={() => setEditDooModal(true)}>
                      Edytuj DOO
                    </Button>
                  )}
                  {["new", "sent"].includes(order.status) && (
                    <Button size="sm" onClick={() => setFeeModal(true)}>
                      <Wallet size={13} className="mr-1" /> {order.fee_id ? "Złóż ponownie" : "Dodaj opłatę"}
                    </Button>
                  )}
                </div>
              ) : undefined
            }
          >
            {order.fee_id ? (
              <div className="space-y-1 text-xs">
                <div className="flex items-center gap-2">
                  <Badge
                    color={
                      order.fee_status === "payment_uploaded" || order.fee_status === "acknowledged"
                        ? "green"
                        : order.fee_status === "rejected" || order.fee_status === "withdrawn"
                          ? "red"
                          : "yellow"
                    }
                  >
                    {FEE_STATUS_LABELS[order.fee_status ?? ""] ?? order.fee_status}
                  </Badge>
                  {order.fee_amount && <span className="font-mono">{order.fee_amount} PLN</span>}
                  <Link to={`/official-fees/${order.fee_id}`} className="hover:underline text-[var(--color-primary)]">
                    otwórz w /official-fees →
                  </Link>
                  {order.fee_payment_files?.map((f) =>
                    f.url ? (
                      <a
                        key={f.id}
                        href={f.url}
                        download={f.original_filename}
                        title={`Pobierz: ${f.original_filename}`}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded bg-red-600 hover:bg-red-700 text-white text-xs font-medium"
                      >
                        <Download size={12} /> PDF
                      </a>
                    ) : null
                  )}
                </div>
                {order.fee_rejection_comment && (
                  <div className="text-red-600">Powód odrzucenia: {order.fee_rejection_comment}</div>
                )}
              </div>
            ) : order.status === "sent" ? (
              <EmptyState>
                Gdy starostwo przyśle DOO, kliknij „Dodaj opłatę” i wgraj tam PDF — kwota, konto
                i tytuł przelewu uzupełnią się automatycznie.
              </EmptyState>
            ) : (
              <EmptyState>
                Opłata opcjonalna — można też od razu oznaczyć wysyłkę (DOO zwykle przychodzi
                dopiero po wysłaniu wniosku do starostwa).
              </EmptyState>
            )}
            {order.doo_file?.url && (
              <div className="mt-2 flex items-center gap-2 text-xs">
                <span className="text-[var(--color-text-secondary)]">DOO ze starostwa:</span>
                <a
                  href={order.doo_file.url}
                  download={order.doo_file.original_filename}
                  title={`Pobierz: ${order.doo_file.original_filename}`}
                  className="inline-flex items-center gap-1 hover:underline text-[var(--color-primary)]"
                >
                  <Download size={12} /> {order.doo_file.original_filename}
                </a>
              </div>
            )}
          </SectionCard>
        </div>

        <div className="space-y-4">
          {/* ── wysyłka / odbiór ── */}
          <SectionCard title="Wysyłka i odbiór">
            <div className="space-y-2 text-xs">
              {["new", "fee_paid"].includes(order.status) && canManage && (
                <Button
                  size="sm"
                  onClick={() =>
                    runAction(() => m.markSent.mutate({ id: order.id }, { onError: onActionError }))
                  }
                  loading={m.markSent.isPending}
                >
                  <Send size={13} className="mr-1" /> Oznacz: wysłano do starostwa
                </Button>
              )}
              {order.sent_at && (
                <div>
                  Wysłano {fmtDateTime(order.sent_at)} ({order.sent_by_name})
                </div>
              )}
              {order.status === "sent" && canManage && (
                <Button
                  size="sm"
                  onClick={() =>
                    runAction(() => m.markReceived.mutate(order.id, { onError: onActionError }))
                  }
                  loading={m.markReceived.isPending}
                >
                  <Inbox size={13} className="mr-1" /> Oznacz: wypisy przyszły
                </Button>
              )}
              {order.received_at && (
                <div>
                  Otrzymano {fmtDateTime(order.received_at)} ({order.received_by_name})
                </div>
              )}
              {order.status === "new" && !order.fee_id && (
                <div className="text-[var(--color-text-muted)]">
                  Wysyłka możliwa bez opłaty albo po jej opłaceniu.
                </div>
              )}
            </div>
          </SectionCard>

          {/* ── pliki z wypisami ── */}
          <SectionCard title={`Pliki ze starostwa (${order.result_files.length})`}>
            <div className="space-y-2">
              {/* Także na zamówieniu zakończonym — starostwo bywa, że dosyła dokument
                  osobną przesyłką po zamknięciu sprawy. Status zostaje „Zakończone". */}
              {["sent", "received", "completed"].includes(order.status) && canManage && (
                <ResultFilesUpload
                  kinds={[
                    { key: "wypisy", label: "Wypisy (PDF/CSV/XLSX/ODS)", accept: ".pdf,.csv,.xlsx,.ods", multiple: true },
                    { key: "inne", label: "Inne pliki (dowolny typ)", multiple: true, other: true },
                  ]}
                  pending={m.uploadResultFiles.isPending}
                  onUpload={(files, other, done) =>
                    runAction(() =>
                      m.uploadResultFiles.mutate(
                        { id: order.id, files, other },
                        { onSuccess: done, onError: onActionError },
                      ),
                    )
                  }
                />
              )}
              {order.result_files.length === 0 ? (
                <EmptyState>Brak plików — wgraj PDF / CSV / XLSX / ODS po otrzymaniu odpowiedzi.</EmptyState>
              ) : (
                <div className="space-y-1">
                  {order.result_files.map((rf) => (
                    <div key={rf.id} className="flex items-center gap-2 text-xs">
                      <Badge color="gray">{rf.kind.toUpperCase()}</Badge>
                      <a
                        href={rf.file.url ?? "#"}
                        download={rf.file.original_filename}
                        className="font-mono hover:underline truncate"
                      >
                        {rf.file.original_filename}
                      </a>
                      {rf.kind === "pdf" && rf.page_count != null && (
                        <span className="text-[var(--color-text-muted)]">{rf.page_count} str.</span>
                      )}
                      {/* Usuwać wolno też na zamówieniu zakończonym — skasowanie
                          OSTATNIEGO pliku cofa status do „Wysłano" (backend). */}
                      {canManage && (
                        <button
                          className="ml-auto text-[var(--color-text-muted)] hover:text-red-600"
                          title="Usuń plik — skasowanie ostatniego pliku ze starostwa cofnie status zamówienia do „Wysłano do starostwa”"
                          onClick={() =>
                            runAction(() =>
                              m.deleteResultFile.mutate(
                                { id: order.id, resultFileId: rf.id },
                                { onError: onActionError },
                              ),
                            )
                          }
                        >
                          <Trash2 size={13} />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </SectionCard>

          {/* ── AI ── */}
          <SectionCard
            title="Analiza AI"
            action={
              order.result_files.length > 0 && ["received", "completed"].includes(order.status) ? (
                <Button
                  size="sm"
                  loading={extracting || m.extract.isPending}
                  onClick={() =>
                    runAction(() =>
                      m.extract.mutate(order.id, {
                        onSuccess: (r) => setExtractTaskId(r.task_id),
                        onError: onActionError,
                      }),
                    )
                  }
                >
                  <Sparkles size={13} className="mr-1" />
                  {order.extraction_proposal ? "Analizuj ponownie" : "Analizuj pliki (AI)"}
                </Button>
              ) : undefined
            }
          >
            {extracting ? (
              <div className="text-xs py-2 space-y-1">
                <div className="flex items-center gap-2">
                  <Spinner size="sm" /> Analizuję pliki…
                </div>
                {/* Ostatnie linie logu bez pustej końcówki: powód („za duży plik”, odmowa
                    modelu) nie może wypaść za „Nie udało się” + „Rozpoznano 0 działek”. */}
                <pre className="text-[10px] text-[var(--color-text-muted)] whitespace-pre-wrap max-h-32 overflow-auto">
                  {extractTask.data?.log?.trim().split("\n").slice(-6).join("\n")}
                </pre>
              </div>
            ) : order.extraction_proposal ? (
              <div className="space-y-2 text-xs">
                <div>
                  Rozpoznano <b>{order.extraction_proposal.parcels.length}</b> działek
                  {order.extracted_at ? ` · ${fmtDateTime(order.extracted_at)}` : ""}
                  {order.extraction_cost_usd
                    ? ` · koszt ${Number(order.extraction_cost_usd).toFixed(4)} USD`
                    : ""}
                </div>
                <ExtractionNotes notes={order.extraction_proposal.notes} />
                {order.status === "completed" && order.applied_at && (
                  <div className="text-green-700">
                    Dane wprowadzone {fmtDateTime(order.applied_at)}.
                  </div>
                )}
                {canManage && (
                  <Button size="sm" variant="secondary" onClick={() => setReviewModal(true)}>
                    {order.status === "completed" ? "Pokaż / popraw dane" : "Przejrzyj i zapisz dane"}
                  </Button>
                )}
              </div>
            ) : extractTask.data?.status === "failure" ? (
              <EmptyState>
                Analiza nie powiodła się — sprawdź pliki i spróbuj ponownie.
                {taskErrorTail(extractTask.data?.error) && (
                  <span className="block mt-1 font-mono text-[10px] normal-case tracking-normal">
                    {taskErrorTail(extractTask.data?.error)}
                  </span>
                )}
              </EmptyState>
            ) : (
              <EmptyState>
                {order.status === "received"
                  ? "Uruchom analizę AI, żeby wyciągnąć właścicieli, KW i użytki z plików."
                  : "Analiza dostępna po oznaczeniu „wypisy przyszły” i wgraniu plików."}
              </EmptyState>
            )}
          </SectionCard>
        </div>
      </div>

      {/* ── historia (pełna szerokość — wpisy bywają długie) ── */}
      <OrderHistorySection events={order.history ?? []} />

      {feeModal && <RequestFeeModal order={order} onClose={() => setFeeModal(false)} />}
      {editDooModal && (
        <EditDooModal
          module="wypis"
          orderNumber={order.number}
          currentFee={
            order.fee_id
              ? { id: order.fee_id, amount: order.fee_amount, status: order.fee_status }
              : null
          }
          currentDooFilename={order.doo_file?.original_filename}
          pending={m.editDoo.isPending}
          onClose={() => setEditDooModal(false)}
          onSubmit={(payload) =>
            runAction(() =>
              m.editDoo.mutate(
                { id: order.id, payload },
                { onSuccess: () => setEditDooModal(false), onError: onActionError },
              ),
            )
          }
          onRequestNew={
            ["new", "sent"].includes(order.status)
              ? () => { setEditDooModal(false); setFeeModal(true); }
              : undefined
          }
        />
      )}
      {poaFeeModal && (
        <WypisPoaFeeModal
          order={order}
          pending={m.setPoaFee.isPending}
          onClose={() => setPoaFeeModal(false)}
          onSubmit={(payload) =>
            runAction(() =>
              m.setPoaFee.mutate(
                { id: order.id, payload },
                { onSuccess: () => setPoaFeeModal(false), onError: onActionError },
              ),
            )
          }
        />
      )}
      {reviewModal && order.extraction_proposal && (
        <ExtractionReviewModal
          source={{
            kind: "order",
            id: order.id,
            proposal: order.extraction_proposal,
            costUsd: order.extraction_cost_usd,
            parcelOptions: order.parcels_brief.map((p) => ({
              value: p.id,
              label: `${p.teryt} (${p.project_name})`,
            })),
            // Aktualni właściciele działek — modal pokazuje, kto zostanie, kto jest
            // nowy, a kto po zapisie stanie się nieaktualny (ponowny wypis).
            currentOwners: Object.fromEntries(
              order.parcels_brief.map((p) => [p.id, p.owners ?? []]),
            ),
            pageCounts: Object.fromEntries(
              order.result_files.map((rf) => [rf.id, rf.page_count ?? 0]),
            ),
            officeBase: "wypis-orders",
            apply: (rows) =>
              m.apply.mutateAsync({ id: order.id, parcels: rows }).then(() => undefined),
            applyPending: m.apply.isPending,
          }}
          onClose={() => setReviewModal(false)}
        />
      )}
    </div>
  );
}
